import { useNavigate } from "react-router-dom";
import { Card, PromptCard } from "@codel/dova-ds";
import type { ActionableRescheduleOfferOut } from "@/__generated__/api/wrenAPI.schemas";
import { offerToCta, type OfferCta } from "./rescheduleCopy";

// The (role, status) -> words table lives in `rescheduleCopy.ts`; this file is
// the RENDERING of that decision — which shape, and where the action routes.

/**
 * Homebase reschedule CTA surface — the resilience mechanism that lets a partner
 * discover and complete an in-flight reschedule without ever receiving/clicking
 * the notification. Renders for BOTH partners: the session stays live at its
 * original time for the whole offer window (synchronous-swap redesign — it never
 * moves to `RESCHEDULING`), so this CTA is the actionable surface either partner
 * uses to respond to a pending reschedule.
 *
 * `partnerName` is the couple's OTHER partner display name (dyad ⇒ the offer's
 * counterpart), or a generic "your partner" — we never render the raw
 * `partner_id` (PII default).
 */
export function RescheduleOfferCtas({
  offers,
  partnerName,
}: {
  offers: ActionableRescheduleOfferOut[];
  partnerName: string;
}) {
  const navigate = useNavigate();
  const ctas = offers
    .map((offer) => ({ offer, cta: offerToCta(offer, partnerName) }))
    .filter((entry): entry is { offer: ActionableRescheduleOfferOut; cta: OfferCta } => entry.cta !== null);

  if (ctas.length === 0) {
    return null;
  }

  return (
    <>
      {ctas.map(({ offer, cta }) =>
        cta.action ? (
          <PromptCard
            key={offer.offer_uuid}
            eyebrow={cta.eyebrow}
            title={cta.title}
            meta={cta.meta}
            actionLabel={cta.action.label}
            onAction={() => navigate(cta.action!.route)}
          />
        ) : (
          <section key={offer.offer_uuid} className="flex flex-col gap-2">
            <div className="dova-eyebrow">{cta.eyebrow}</div>
            <Card className="flex flex-col gap-2 p-6">
              <h2 className="m-0 [font:var(--type-h2)] text-primary">{cta.title}</h2>
              <div className="[font:var(--type-body)] text-secondary">{cta.meta}</div>
            </Card>
          </section>
        ),
      )}
    </>
  );
}
